import { create } from "zustand";
import type { User } from "@/api/types";
import { authService } from "@/api/services";
import {
  clearAccessToken,
  getAccessToken,
  notifySignedOut,
  onAuthChannelMessage,
  refreshAccessToken,
  setAccessToken,
} from "@/api/token";
import { ApiError } from "@/api/errors";
import { usernameFromName } from "@/api/resource";

export type AuthStatus =
  | "unknown" // session restore not attempted yet
  | "restoring" // attempting refresh-cookie restore
  | "authenticated"
  | "unauthenticated";

interface AuthState {
  status: AuthStatus;
  user: User | null;

  /** Attempt to restore the session from the refresh cookie. */
  restore: () => Promise<void>;
  /** Complete a password sign-in. Returns the signed-in user. */
  signInWithPassword: (username: string, password: string) => Promise<User>;
  /** Complete an SSO sign-in after the OAuth callback. */
  signInWithSso: (input: { idpName: string; code: string; redirectUri: string; codeVerifier?: string }) => Promise<User>;
  /** Sign out: tell the server, then always clear local state. */
  signOut: () => Promise<void>;
  /** Re-fetch the current user (after profile changes). */
  refreshCurrentUser: () => Promise<void>;
  /** Mark unauthenticated and clear state (e.g. after a failed refresh elsewhere). */
  forceUnauthenticated: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "unknown",
  user: null,

  restore: async () => {
    if (get().status === "restoring" || get().status === "authenticated") return;
    set({ status: "restoring" });
    try {
      const token = await refreshAccessToken();
      if (!token) {
        set({ status: "unauthenticated", user: null });
        return;
      }
      const { user } = await authService.getCurrentUser();
      set({ status: user ? "authenticated" : "unauthenticated", user: user ?? null });
    } catch {
      clearAccessToken();
      set({ status: "unauthenticated", user: null });
    }
  },

  signInWithPassword: async (username, password) => {
    const res = await authService.signIn({ passwordCredentials: { username, password } });
    if (res.accessToken) setAccessToken(res.accessToken, res.accessTokenExpiresAt);
    let user = res.user ?? null;
    if (!user) {
      const me = await authService.getCurrentUser();
      user = me.user ?? null;
    }
    set({ status: user ? "authenticated" : "unauthenticated", user });
    if (!user) throw new ApiError({ message: "Sign-in succeeded but user is unavailable", httpStatus: 200 });
    return user;
  },

  signInWithSso: async ({ idpName, code, redirectUri, codeVerifier }) => {
    const res = await authService.signIn({
      ssoCredentials: { idpName, code, redirectUri, ...(codeVerifier ? { codeVerifier } : {}) },
    });
    if (res.accessToken) setAccessToken(res.accessToken, res.accessTokenExpiresAt);
    let user = res.user ?? null;
    if (!user) {
      const me = await authService.getCurrentUser();
      user = me.user ?? null;
    }
    set({ status: user ? "authenticated" : "unauthenticated", user });
    if (!user) throw new ApiError({ message: "SSO sign-in succeeded but user is unavailable", httpStatus: 200 });
    return user;
  },

  signOut: async () => {
    try {
      await authService.signOut();
    } catch {
      // Even if the server call fails we must clear local state (PRD 8.2.7).
    } finally {
      notifySignedOut();
      set({ status: "unauthenticated", user: null });
    }
  },

  refreshCurrentUser: async () => {
    try {
      const { user } = await authService.getCurrentUser();
      if (user) set({ user });
    } catch {
      // Keep stale user on transient failures.
    }
  },

  forceUnauthenticated: () => {
    clearAccessToken();
    set({ status: "unauthenticated", user: null });
  },
}));

/** Subscribe to cross-tab auth events and mirror them into the store. */
export function bindAuthChannelSync(): () => void {
  return onAuthChannelMessage((msg) => {
    if (msg.type === "signed-out" || msg.type === "refresh-failed") {
      useAuthStore.getState().forceUnauthenticated();
    } else if (msg.type === "refreshed") {
      // Token was adopted by token.ts; refresh the current user if we have one.
      const { status, refreshCurrentUser } = useAuthStore.getState();
      if (status === "authenticated") void refreshCurrentUser();
    }
  });
}

// Convenience selectors ----------------------------------------------------

export const useAuthStatus = () => useAuthStore((s) => s.status);
export const useCurrentUser = () => useAuthStore((s) => s.user);
export const useIsAuthenticated = () => useAuthStore((s) => s.status === "authenticated");
export const useIsAdmin = () => useAuthStore((s) => s.user?.role === "ADMIN");
export const useCurrentUsername = () => useAuthStore((s) => (s.user ? usernameFromName(s.user.name) : ""));

/** Ensure a session exists (used by route guards). Attempts restore if unknown. */
export async function requireSession(): Promise<User | null> {
  const { status, user, restore } = useAuthStore.getState();
  if (status === "authenticated") return user;
  if (status === "unknown") {
    await restore();
    return useAuthStore.getState().user;
  }
  // Try a silent refresh once in case the tab lost its in-memory token.
  if (!getAccessToken()) {
    try {
      await refreshAccessToken();
    } catch {
      return null;
    }
    await useAuthStore.getState().refreshCurrentUser();
  }
  return useAuthStore.getState().user;
}
